<template>
  <!-- The room's door (mystic room 2026-10-30, DESIGN.md §2): tonight's sky
       mark (on a marked day, the day: holiday-line DESIGN.md §2), the Mystic's line written word by word beside its gold rule, then
       ONE gold button. Get Reading goes straight into the grounding; "for me ·
       for someone" is asked after it, in the chat (Chris 2026-10-30). The
       portrait is the way to About (it turns over). Nothing under the button:
       no "A reading for someone" link (Chris 2026-10-30, more than once).
       On Get Reading the line drifts up like smoke and three gold dots wait
       where it was. -->
  <div
    class="room-door"
    :class="{
      'is-arrive': playing, 'is-waiting': waiting, 'is-finished': finished && scene === 'arrive',
      'is-leaving': leaving, 'is-flipped': flipped, 'is-reduced': reduced,
    }"
    :style="{ '--cta-at': `${stream.ctaAt}ms`, '--line-ms': `${Math.max(300, stream.lineEnd - 1100)}ms` }"
    data-testid="room-door"
  >
    <div ref="lineBox" class="room-line-box">
      <!-- The holiday line (2026-10-30): on a marked day the sky line names
           the day and is a button with the card's ↻; it turns the card to
           the day. A solemn day is never marked (no day from the server). -->
      <button
        v-if="sky && sky.label && sky.day"
        ref="skyEl"
        type="button"
        class="room-sky is-day"
        :class="{ 'is-glint': glint && !reduced }"
        data-testid="room-sky"
        :aria-label="skyAria"
        :title="bothNames || undefined"
        :aria-expanded="dayOpen ? 'true' : 'false'"
        @click.stop="onDay"
      >
        <!-- Two marks on one day (2026-10-31): both glyphs, small, side by side. -->
        <span v-if="bothNames" class="room-sky-glyphs" aria-hidden="true">
          <DayGlyph v-for="m in skyMarks" :key="m.key" class="room-sky-glyph" :kind="m.glyph || 'fullmoon'" :at="Number.isInteger(m.at) ? m.at : -1" still />
        </span>
        <svg v-else :width="moonBox" :height="moonBox" :viewBox="`${-moonR - 1} ${-moonR - 1} ${moonBox} ${moonBox}`" aria-hidden="true">
          <circle :r="moonR" fill="rgba(240,217,163,.16)" stroke="rgba(240,217,163,.5)" stroke-width=".8" />
          <path :d="moonPath" fill="#f0d9a3" />
        </svg>
        <span class="room-sky-label">{{ skyLabel }}</span>
        <span class="room-sky-turn" aria-hidden="true">↻</span>
        <span class="room-sky-glint" aria-hidden="true"></span>
      </button>
      <p v-else-if="sky && sky.label" class="room-sky" data-testid="room-sky">
        <svg :width="moonBox" :height="moonBox" :viewBox="`${-moonR - 1} ${-moonR - 1} ${moonBox} ${moonBox}`" aria-hidden="true">
          <circle :r="moonR" fill="rgba(240,217,163,.16)" stroke="rgba(240,217,163,.5)" stroke-width=".8" />
          <path :d="moonPath" fill="#f0d9a3" />
        </svg>
        <span>{{ sky.label }}</span>
      </p>
      <div ref="linebox" class="room-linebox" :style="lineCapStyle">
        <!-- The line changed while the room was up (Talk it through's parting
             line, 2026-10-30): the old one fades out over the new one. -->
        <p v-if="swapFrom" class="room-greeting room-greeting-out" data-testid="room-greeting-out" aria-hidden="true">
          <span class="room-rule"></span>{{ swapFrom }}
        </p>
        <p v-if="greeting" :key="greeting" class="room-greeting" :class="{ 'is-in': swapped }" data-testid="room-greeting">
          <span class="room-rule" aria-hidden="true"></span>
          <span class="room-sr">{{ greeting }}</span>
          <span aria-hidden="true"><template v-for="(w, i) in stream.words" :key="i"><span class="room-w" :style="wordStyle(w, i)">{{ w.text }}</span>{{ i < stream.words.length - 1 ? ' ' : '' }}</template></span>
        </p>
        <div v-if="leaving" class="room-dots" data-testid="room-dots"><BreathDots /></div>
      </div>
    </div>

    <div class="room-cta-row" :style="{ transform: `translateY(${ctaShift}px)` }">
      <button
        type="button"
        class="room-cta"
        :class="{ 'is-ready': ctaReady }"
        data-testid="room-get-reading"
        :tabindex="ctaReady && !leaving ? 0 : -1"
        :aria-hidden="ctaReady ? 'false' : 'true'"
        @click="onGet"
      >
        <span class="room-cta-shim" aria-hidden="true"></span>
        <span class="room-cta-mark" aria-hidden="true">{{ face.mark }}</span>
        <span class="room-cta-text">{{ face.text }}</span>
        <span v-if="face.price" class="room-cta-price">{{ face.price }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import BreathDots from '@/components/ui/BreathDots.vue'
import DayGlyph from '@/components/ui/DayGlyph.vue'
import { roomCtaFace, wordStream } from '@/utils/mystic-room'

const props = defineProps({
  name: { type: String, default: '' },
  greeting: { type: String, default: '' },
  price: { type: String, default: '' },
  // The first reading is free (the front door's check): no price shown.
  free: { type: Boolean, default: false },
  // An unfinished reading: "Continue your reading", no price.
  continuing: { type: Boolean, default: false },
  // { label, moon: { lit, waxing } } from utils/mystic-room skyMark.
  sky: { type: Object, default: null },
  // 'arrive' | 'settled' | 'none'
  scene: { type: String, default: 'settled' },
  // The arrival's t = 0: the portrait decoded and the loading overlay gone.
  started: { type: Boolean, default: true },
  // Tapped while the line was writing: finish it at once.
  finished: { type: Boolean, default: false },
  leaving: { type: Boolean, default: false },
  flipped: { type: Boolean, default: false },
  // How far the button glides down to sit under the card's back.
  ctaShift: { type: Number, default: 0 },
  reduced: { type: Boolean, default: false },
  // The most the sky mark and line may take (roomLayout's lineMax); 0 = no limit.
  lineMax: { type: Number, default: 0 },
  // The card is turned to the day (the sky line's aria-expanded).
  dayOpen: { type: Boolean, default: false },
  // The day's one glint across its name (first visit of that day on this device).
  glint: { type: Boolean, default: false },
})
const emit = defineEmits(['get-reading', 'arrived', 'day'])

function onDay () {
  if (props.leaving) return
  emit('day')
}

// Two marks on one day (2026-10-31, "We can have both"): the main mark and
// the day's `also`; the button's aria-label and title name them all.
const skyMarks = computed(() => {
  const d = props.sky && props.sky.day
  return d ? [d, ...(Array.isArray(d.also) ? d.also : [])] : []
})
const andList = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`)
const bothNames = computed(() => {
  const names = (props.sky && props.sky.names) || []
  return names.length > 1 ? andList(names) : ''
})
const skyAria = computed(() => {
  const first = String((props.sky && ((props.sky.labels && props.sky.labels[0]) || props.sky.label)) || '')
  if (!bothNames.value) return `${first.replace(' · ', ', ')}. About this day.`
  const when = first.split(' · ').slice(1).join(' · ')
  return `${bothNames.value}${when ? `, ${when}` : ''}. About this day.`
})

const face = computed(() => roomCtaFace({ price: props.price, free: props.free, continuing: props.continuing }))
const stream = computed(() => wordStream(props.greeting))
const playing = computed(() => props.scene === 'arrive' && props.started && !props.reduced)
const waiting = computed(() => props.scene === 'arrive' && !props.started && !props.reduced)

// Get Reading arrives last; it cannot be pressed before it is there.
const arrivedByTime = ref(false)
let ctaTimer = null
watch(() => [props.scene, props.started, props.reduced], () => {
  clearTimeout(ctaTimer)
  if (props.scene !== 'arrive' || props.reduced) { arrivedByTime.value = true; return }
  if (!props.started) { arrivedByTime.value = false; return }
  ctaTimer = setTimeout(() => { arrivedByTime.value = true }, stream.value.ctaAt)
}, { immediate: true })
onBeforeUnmount(() => clearTimeout(ctaTimer))
const ctaReady = computed(() => arrivedByTime.value || props.finished)
watch(ctaReady, (on) => { if (on) emit('arrived') }, { immediate: true })

function onGet () {
  if (!ctaReady.value || props.leaving) return
  emit('get-reading')
}

// Each word: its moment in the stream; on leaving, its own drift up and sideways.
function wordStyle (w, i) {
  const a = Math.sin(i * 12.9898) * 0.5 + 0.5
  const b = Math.sin(i * 78.233)
  return {
    '--at': `${w.at}ms`,
    '--i': i,
    '--sy': `${-(22 + a * 10)}px`,
    '--sx': `${(b * 5).toFixed(1)}px`,
  }
}

// The drawn moon: a half disc on the lit side plus the terminator ellipse.
const moonR = 6.5
const moonBox = moonR * 2 + 2
const moonPath = computed(() => {
  const m = (props.sky && props.sky.moon) || { lit: 0.5, waxing: true }
  const r = moonR
  const rx = Math.abs(2 * m.lit - 1) * r
  const side = m.waxing ? 1 : -1
  const gib = m.lit > 0.5
  return `M0,${-r} A${r},${r} 0 0,${side > 0 ? 1 : 0} 0,${r} A${rx},${r} 0 0,${(side > 0) === gib ? 1 : 0} 0,${-r} Z`
})

const lineBox = ref(null)
const skyEl = ref(null)
defineExpose({
  lineHeight: () => (lineBox.value ? lineBox.value.offsetHeight : 0),
  lineEl: () => lineBox.value,
  // Focus comes back to the sky line when the day's card turns back.
  focusSky: () => { if (skyEl.value && skyEl.value.focus) skyEl.value.focus({ preventScroll: true }) },
})

// ── The sky line never wraps: the first of its labels that fits (the
// weekday drops first, then the short name). Measured on an unseen copy so
// the line itself never jumps.
const fitIdx = ref(0)
const skyLabel = computed(() => {
  const tries = (props.sky && props.sky.labels) || []
  return tries[Math.min(fitIdx.value, tries.length - 1)] || (props.sky && props.sky.label) || ''
})
function fitSky () {
  const el = skyEl.value
  const box = lineBox.value
  const tries = (props.sky && props.sky.labels) || []
  if (!el || !box || tries.length < 2 || !box.clientWidth) { fitIdx.value = 0; return }
  const copy = el.cloneNode(true)
  copy.removeAttribute('data-testid')
  Object.assign(copy.style, { position: 'absolute', visibility: 'hidden', width: 'max-content', maxWidth: 'none', left: '0', top: '0', animation: 'none' })
  box.appendChild(copy)
  const label = copy.querySelector('.room-sky-label')
  let pick = tries.length - 1
  for (let i = 0; i < tries.length; i++) {
    label.textContent = tries[i]
    if (copy.offsetWidth <= box.clientWidth) { pick = i; break }
  }
  box.removeChild(copy)
  fitIdx.value = pick
}
onMounted(() => {
  nextTick(fitSky)
  if (typeof window !== 'undefined') window.addEventListener('resize', fitSky)
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) document.fonts.ready.then(fitSky, () => {})
})
onBeforeUnmount(() => { if (typeof window !== 'undefined') window.removeEventListener('resize', fitSky) })
watch(() => props.sky, () => nextTick(fitSky))

// ── The line changes while the room is up (Talk it through's parting line,
// fix 2026-10-30): a crossfade, the new line whole (no second word-by-word
// write); reduced motion swaps at once. A line arriving where there was none,
// or before the arrival starts, is the ordinary line.
const swapFrom = ref('')
const swapped = ref(false)
let swapTimer = null
watch(() => props.greeting, (next, prev) => {
  if (!prev || !next || next === prev || waiting.value) return
  if (props.reduced) { swapFrom.value = ''; return }
  swapFrom.value = prev
  swapped.value = true
  clearTimeout(swapTimer)
  swapTimer = setTimeout(() => { swapFrom.value = '' }, 700)
})
onBeforeUnmount(() => clearTimeout(swapTimer))

// ── A line taller than the stage allows scrolls in its own box, so Get
// Reading stays above the bar. Only then: a box would clip the smoke.
const linebox = ref(null)
const capped = ref(0)
function checkCap () {
  const el = linebox.value
  const sky = lineBox.value && lineBox.value.querySelector('.room-sky')
  const max = props.lineMax > 0 ? props.lineMax - (sky ? sky.offsetHeight + 12 : 0) : 0
  capped.value = max > 0 && el && el.scrollHeight > max + 1 ? max : 0
}
onMounted(() => nextTick(checkCap))
watch(() => [props.greeting, props.lineMax, props.sky], () => nextTick(checkCap))
const lineCapStyle = computed(() => (capped.value ? { maxHeight: `${capped.value}px`, overflowY: 'auto' } : null))
</script>

<style scoped>
.room-door { position: relative; display: flex; flex-direction: column; gap: 26px; width: 100%; pointer-events: none; }
/* Only the buttons take a tap: the card's back sits over the door's place when turned. */
.room-cta.is-ready { pointer-events: auto; }
.room-line-box { position: relative; transition: opacity 0.24s ease, visibility 0s linear 0s; }
.room-door.is-flipped .room-line-box { opacity: 0; visibility: hidden; transition: opacity 0.24s ease, visibility 0s linear 0.24s; }
.room-door:not(.is-flipped) .room-line-box { transition: opacity 0.3s ease 0.3s; }

/* The sky mark: tonight's moon and the day, small caps, gold at 82%. */
.room-sky {
  margin: 0 0 12px; padding-left: 20px; display: flex; align-items: center; gap: 8px; white-space: nowrap;
  font-family: 'Cinzel', serif; font-size: 11.5px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase;
  color: rgba(240, 217, 163, 0.82); opacity: 0.9;
}
.room-sky svg { flex: 0 0 auto; }
/* Two marks: both glyphs at the moon's size, side by side. */
.room-sky-glyphs { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 3px; }
.room-sky .room-sky-glyph { width: 15px; height: 15px; margin: 0; }
/* A marked day: the same line as a button, plus the card's ↻. The tap area is
   44px tall (12px of unseen padding above and below, taken back by the
   margins), so nothing moves. Hover or focus: full gold. */
button.room-sky {
  position: relative; overflow: hidden; pointer-events: auto; cursor: pointer; background: none; border: 0;
  border-radius: 8px; padding-top: 12px; padding-bottom: 12px; padding-right: 2px; margin-top: -12px; margin-bottom: 0;
  min-height: 44px; max-width: 100%; text-align: left;
}
button.room-sky:hover, button.room-sky:focus-visible { color: #f0d9a3; opacity: 1; }
button.room-sky:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.room-sky-label { overflow: hidden; text-overflow: ellipsis; }
.room-sky-turn {
  flex: 0 0 auto; width: 19px; height: 19px; margin-left: 2px; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; line-height: 1; letter-spacing: 0;
  border: 1px solid rgba(240, 217, 163, 0.5); background: rgba(10, 5, 22, 0.5); color: #f0d9a3;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.room-door.is-flipped .room-sky-turn { transform: rotate(180deg); }
.room-sky-glint {
  position: absolute; inset: 0; pointer-events: none; transform: translateX(-110%); mix-blend-mode: screen;
  background: linear-gradient(100deg, transparent 30%, rgba(255, 236, 190, 0) 40%, rgba(255, 236, 190, 0.7) 50%, rgba(255, 236, 190, 0) 60%, transparent 70%);
}
/* After the button's shimmer, one soft glint crosses the day's name, once. */
.room-door.is-arrive .room-sky.is-glint .room-sky-glint { animation: room-sky-glint 1.1s ease-in-out calc(var(--cta-at) + 2.2s) both; }
.room-door:not(.is-arrive):not(.is-waiting) .room-sky.is-glint .room-sky-glint { animation: room-sky-glint 1.1s ease-in-out 2.2s both; }
@keyframes room-sky-glint { from { transform: translateX(-110%); } to { transform: translateX(110%); } }
.room-linebox { position: relative; }
/* The line: the reader line's look, unboxed, the gold rule brightest where it begins. */
.room-greeting {
  position: relative; margin: 0; padding: 1px 0 1px 20px;
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 21px; line-height: 1.55; color: #efe6ff;
}
.room-rule {
  position: absolute; left: 0; top: 4px; bottom: 4px; width: 2px; border-radius: 2px; transform-origin: 50% 0;
  background: linear-gradient(180deg, #f0d9a3 0%, rgba(240, 217, 163, 0.45) 38%, rgba(240, 217, 163, 0.05) 100%);
}
.room-w { display: inline-block; white-space: pre; }
.room-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
/* The line swapped while the room is up: the old one fades out over the new. */
.room-greeting-out { position: absolute; top: 0; left: 0; right: 0; pointer-events: none; animation: room-line-out 0.6s ease both; }
.room-greeting.is-in { animation: room-fade-in 0.6s ease both; }
.room-door:not(.is-leaving) .room-greeting.is-in .room-w,
.room-door:not(.is-leaving) .room-greeting.is-in .room-rule { animation: none; opacity: 1; transform: none; filter: none; }
@keyframes room-line-out { from { opacity: 1; } to { opacity: 0; } }
.room-dots {
  position: absolute; left: 0; right: 0; top: 50%; transform: translateY(-50%);
  display: flex; justify-content: center; pointer-events: none;
}

.room-cta-row {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.room-cta {
  position: relative; overflow: hidden; white-space: nowrap;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 48px; padding: 10px 24px; border: none; border-radius: 999px;
  font-family: 'Cinzel', serif; font-size: 15px; font-weight: 800; letter-spacing: 0.04em; font-variant: small-caps;
  color: #2a1a05; background: linear-gradient(180deg, #f5d98a, #d9a93f);
  box-shadow: 0 10px 24px -10px rgba(217, 169, 63, 0.85); cursor: pointer;
}
.room-cta:not(.is-ready) { pointer-events: none; }
.room-cta.is-ready { animation: room-cta-breath 3.6s ease-in-out infinite; }
.room-cta:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.room-cta-mark { font-size: 14px; line-height: 1; }
.room-cta-price {
  display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 999px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; font-variant: normal;
  color: #f5e6bd; background: rgba(42, 26, 5, 0.55);
}
.room-cta-shim {
  position: absolute; inset: 0; pointer-events: none; transform: translateX(-130%); opacity: 0;
  background: linear-gradient(105deg, transparent 30%, rgba(255, 255, 255, 0.65) 50%, transparent 70%);
}
@keyframes room-cta-breath {
  0%, 100% { box-shadow: 0 10px 24px -10px rgba(217, 169, 63, 0.85); }
  50% { box-shadow: 0 10px 34px -6px rgba(240, 200, 110, 1), 0 0 0 1px rgba(255, 230, 170, 0.35); }
}

/* ── The arrival (t = 0: the portrait decoded and the overlay gone) ── */
.room-door.is-waiting .room-sky, .room-door.is-waiting .room-w, .room-door.is-waiting .room-cta { opacity: 0; }
.room-door.is-waiting .room-rule { transform: scaleY(0); }
.room-door.is-arrive .room-sky { animation: room-sky-in 0.6s cubic-bezier(0.33, 1, 0.68, 1) 0.7s both; }
.room-door.is-arrive .room-w { animation: room-word-in 0.38s ease-out var(--at) both; }
.room-door.is-arrive .room-rule { animation: room-rule-draw var(--line-ms) cubic-bezier(0.33, 1, 0.68, 1) 1.1s both; }
.room-door.is-arrive .room-cta { animation: room-cta-in 0.6s cubic-bezier(0.33, 1, 0.68, 1) var(--cta-at) both, room-cta-breath 3.6s ease-in-out calc(var(--cta-at) + 2s) infinite; }
.room-door.is-arrive .room-cta-shim { animation: room-shim 1.4s ease-in-out calc(var(--cta-at) + 0.6s) both; }
/* Tapped while the line writes: it finishes at once and the button arrives. */
.room-door.is-arrive.is-finished .room-sky, .room-door.is-arrive.is-finished .room-w, .room-door.is-arrive.is-finished .room-rule { animation: none; }
.room-door.is-arrive.is-finished .room-cta { animation: room-cta-in 0.6s cubic-bezier(0.33, 1, 0.68, 1) both, room-cta-breath 3.6s ease-in-out 2s infinite; }
.room-door.is-arrive.is-finished .room-cta-shim { animation: room-shim 1.4s ease-in-out 0.6s both; }
@keyframes room-sky-in {
  from { opacity: 0; letter-spacing: 0.34em; }
  to { opacity: 0.9; }
}
@keyframes room-word-in {
  from { opacity: 0; transform: translateY(3px); filter: blur(2px); }
  to { opacity: 1; transform: none; filter: none; }
}
@keyframes room-rule-draw { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes room-cta-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes room-shim {
  0% { transform: translateX(-130%); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateX(130%); opacity: 0; }
}

/* ── Get Reading: the greeting drifts up like smoke, the dots wait where it was ── */
.room-door.is-leaving .room-cta { animation: room-cta-out 0.32s ease both; pointer-events: none; }
.room-door.is-leaving .room-sky { animation: room-sky-out 0.5s ease both; }
.room-door.is-leaving .room-w { animation: room-smoke 1s ease-in-out calc(var(--i) * 16ms) both; }
.room-door.is-leaving .room-rule { animation: room-rule-out 0.6s ease both; }
.room-door.is-leaving .room-dots { animation: room-fade-in 0.5s ease 0.65s both; }
@keyframes room-cta-out { from { opacity: 1; } to { opacity: 0; transform: translateY(6px); } }
@keyframes room-sky-out { from { opacity: 0.9; } to { opacity: 0; transform: translateY(-6px); } }
@keyframes room-smoke {
  from { opacity: 1; transform: none; filter: none; }
  to { opacity: 0; transform: translate(var(--sx), var(--sy)); filter: blur(6px); }
}
@keyframes room-rule-out { from { transform: scaleY(1); } to { transform: scaleY(0); } }
@keyframes room-fade-in { from { opacity: 0; } to { opacity: 1; } }

@media (max-width: 420px) {
  .room-sky { font-size: 10.5px; gap: 7px; letter-spacing: 0.15em; }
  .room-greeting { font-size: 18.5px; }
  .room-cta { padding: 10px 18px; font-size: 14px; }
}
@media (max-width: 699px) {
  .room-greeting { font-size: 18.5px; }
}
/* Reduced motion: everything is simply there; Get Reading swaps the line for the dots at once. */
.room-door.is-reduced .room-sky, .room-door.is-reduced .room-w, .room-door.is-reduced .room-rule,
.room-door.is-reduced .room-cta, .room-door.is-reduced .room-cta-shim, .room-door.is-reduced .room-dots,
.room-door.is-reduced .room-sky-glint { animation: none !important; }
.room-door.is-reduced .room-sky-turn { transition: none; }
.room-door.is-reduced.is-leaving .room-greeting, .room-door.is-reduced.is-leaving .room-sky, .room-door.is-reduced.is-leaving .room-cta { visibility: hidden; }
.room-door.is-reduced .room-cta-row, .room-door.is-reduced .room-line-box { transition: none; }
</style>
